import type React from 'react';
import { type Dispatch, type SetStateAction, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAtom } from 'jotai';
import { Search } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { env } from '@/constants/env';
import { cn } from '@/lib/utils';

import { desiredStyleSettingsAtom, settingsChangedAtom } from '../../atoms/settings';
import {
  DESIRED_STYLE_OPTIONS,
  type TYPE_DESIRED_STYLE_OPTIONS,
} from '../../constants/style-settings';

export interface DesiredStyleSelectionContentProps {
  setPopoverOpen: Dispatch<SetStateAction<boolean>>;
}

const desiredStyleOptions = [
  { value: DESIRED_STYLE_OPTIONS.ELEGENT, image: 'default-imagerendering-style_Electical' }, // TODO: Correct image, as soon as we have it
  {
    value: DESIRED_STYLE_OPTIONS.SUSTAINABLE_DESIGN,
    image: 'default-imagerendering-style_SustainableDesign',
  },
  {
    value: DESIRED_STYLE_OPTIONS.CURVES_AND_ORGANIC_SHAPES,
    image: 'default-imagerendering-style_CurvesAndOrganic',
  },
  {
    value: DESIRED_STYLE_OPTIONS.RETRO_AND_VINTAGE,
    image: 'default-imagerendering-style_RetroAndVintage',
  },
  { value: DESIRED_STYLE_OPTIONS.MINIMALIST, image: 'default-imagerendering-style_Minimalist' },
  { value: DESIRED_STYLE_OPTIONS.INDUSTRIAL, image: 'default-imagerendering-style_Industrial' },
  { value: DESIRED_STYLE_OPTIONS.SCANDINAVIAN, image: 'default-imagerendering-style_Scandinavian' },
  { value: DESIRED_STYLE_OPTIONS.RUSTIC, image: 'default-imagerendering-style_Rustic' },
  { value: DESIRED_STYLE_OPTIONS.CONTEMPORARY, image: 'default-imagerendering-style_Contemporary' },
  {
    value: DESIRED_STYLE_OPTIONS.MID_CENTURY_MODERN,
    image: 'default-imagerendering-style_MidCerntury',
  },
  {
    value: DESIRED_STYLE_OPTIONS.FRENCH_COUNTRY,
    image: 'default-imagerendering-style_FrenchCountry',
  },
  { value: DESIRED_STYLE_OPTIONS.COASTAL, image: 'default-imagerendering-style_Coastal' },
  { value: DESIRED_STYLE_OPTIONS.ART_DECO, image: 'default-imagerendering-style_ArtDeco' },
  { value: DESIRED_STYLE_OPTIONS.BOHO_CHIC, image: 'default-imagerendering-style_BohoChic' },
  { value: DESIRED_STYLE_OPTIONS.URBAN_MODERN, image: 'default-imagerendering-style_UrbanModern' },
  {
    value: DESIRED_STYLE_OPTIONS.MEDITERRANEAN,
    image: 'default-imagerendering-style_Mediterranean',
  },
  { value: DESIRED_STYLE_OPTIONS.MOROCCAN, image: 'default-imagerendering-style_Moroccan' },
  { value: DESIRED_STYLE_OPTIONS.VICTORIAN, image: 'default-imagerendering-style_Victorian' },
];

export const DesiredStyleSelectionContent = ({
  setPopoverOpen,
}: DesiredStyleSelectionContentProps) => {
  const [desiredStyle, setDesiredStyle] = useAtom(desiredStyleSettingsAtom);
  const [, setSettingsChanged] = useAtom(settingsChangedAtom);
  const { t } = useTranslation();
  const [search, setSearch] = useState<string | undefined>();

  const filteredDesiredStyleOptions = useMemo(
    () =>
      !search
        ? desiredStyleOptions
        : desiredStyleOptions.filter((option) =>
            t(option.value).toLowerCase().includes(search.toLowerCase())
          ),
    [t, search]
  );

  const handleSelect = (option: TYPE_DESIRED_STYLE_OPTIONS) => {
    setDesiredStyle(option);
    setPopoverOpen(false);
    setSettingsChanged(true);
  };

  return (
    <div className="w-full">
      <div className="flex h-[40px] items-center justify-between rounded-b-sm bg-text-primary pl-5 pr-6 lg:h-[60px]">
        <span className="text-sm font-semibold text-white">{t('desiredStyle')}</span>
      </div>
      <div className="bg-white p-2 sm:px-5 sm:py-6">
        <Input
          size="sm"
          className="border border-solid border-primary-light"
          placeholder={t('search')}
          value={search}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
          startIcon={<Search className="text-primary-light" />}
        />
        <div className="mt-3 grid max-h-calc-full-minus-100 grid-cols-2 gap-2 overflow-y-auto sm:max-h-[400px] lg:max-h-[472px]">
          {filteredDesiredStyleOptions.map((option, index) => {
            const isSelected = option.value === desiredStyle;

            return (
              <button
                type="button"
                key={`desired-style-option-${String.fromCharCode(65 + index)}`}
                className={cn(
                  'relative flex cursor-pointer flex-col overflow-hidden rounded-sm border border-solid',
                  !isSelected ? 'border-text-primary' : 'border-primary-light'
                )}
                onClick={() => handleSelect(option.value)}
              >
                <div className="flex-1">
                  <img
                    className="h-[84px] w-full object-cover"
                    src={`${env.API_URL}/images/${option.image}?size=thumbnail500`}
                    alt={`Desired-Style-${option.image}`}
                  />
                </div>
                <div
                  className={cn(
                    'z-2 flex h-[30px] items-center justify-center',
                    isSelected ? 'bg-primary-light' : 'bg-text-primary'
                  )}
                >
                  <span className="text-center text-sm font-semibold leading-none text-white">
                    {t(option.value)}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
